import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useLocation } from "react-router-dom";
import { fetchProductsLite, fetchSiteConfig } from "@/lib/api";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";

const FALLBACK_CATEGORIES = ["GLP-1s", "Healing Peptides", "HGH & Ancillaries", "Nootropics", "Lab Supplies"];

/** Collapse variant_group products to one representative per group (pick best_value if set, else min price). */
function dedupeByVariantGroup(products) {
    const groups = new Map();
    const result = [];
    for (const p of products) {
        if (!p.variant_group) {
            result.push({ ...p, _variantCount: 0 });
            continue;
        }
        const existing = groups.get(p.variant_group);
        if (!existing) {
            groups.set(p.variant_group, { product: p, members: [p] });
        } else {
            existing.members.push(p);
            // Prefer best_value; else lowest order
            if (p.best_value && !existing.product.best_value) existing.product = p;
            else if (!existing.product.best_value && (p.variant_order || 99) < (existing.product.variant_order || 99)) existing.product = p;
        }
    }
    for (const { product, members } of groups.values()) {
        const prices = members.map((m) => m.price_usd);
        const minPrice = Math.min(...prices);
        // Representative: prefer the lowest-price variant as default click target so "from" price matches
        const representative = members.find((m) => m.price_usd === minPrice) || product;
        result.push({
            ...representative,
            price_usd: minPrice,
            _variantCount: members.length,
            _variantLabels: members.sort((a, b) => (a.variant_order || 0) - (b.variant_order || 0)).map((m) => m.variant_label || m.dosage),
            _anyInStock: members.some((m) => m.in_stock !== false),
        });
    }
    return result;
}

export default function Shop() {
    const [searchParams, setSearchParams] = useSearchParams();
    const cat = searchParams.get("cat") || "All";
    // Seed from localStorage cache so the catalog paints instantly on revisit / mobile returns.
    // Fresh data from /api/products updates it within ~200ms and replaces the cached render.
    const [products, setProducts] = useState(() => {
        try {
            const cached = JSON.parse(localStorage.getItem("hxr_products_cache_v1") || "null");
            if (cached && Array.isArray(cached.products) && cached.ts && (Date.now() - cached.ts) < 24 * 60 * 60 * 1000) {
                return cached.products;
            }
        } catch { /* ignore */ }
        return [];
    });
    const [loading, setLoading] = useState(products.length === 0);
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
    const location = useLocation();

    useEffect(() => {
        let attempts = 0;
        const load = () => {
            attempts += 1;
            fetchProductsLite().then((p) => {
                setProducts(p);
                setLoading(false);
                try { localStorage.setItem("hxr_products_cache_v1", JSON.stringify({ products: p, ts: Date.now() })); } catch { /* ignore */ }
            }).catch(() => {
                // Network/cache failure recovery (e.g. ERR_CACHE_WRITE_FAILURE after age-gate refresh).
                if (attempts < 3) setTimeout(load, attempts * 800);
                else setLoading(false);
            });
        };
        load();
        fetchSiteConfig().then((c) => {
            const cats = (c?.catalog_filters || []).map((f) => f.cat).filter((c) => c && c !== "All");
            if (cats.length) setCategories(cats);
        }).catch(() => {});
    }, []);

    // Scroll to top when navigating to /shop or when category changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [location.pathname, cat]);

    const deduped = useMemo(() => dedupeByVariantGroup(products), [products]);
    const filtered = useMemo(() => {
        if (cat === "All") return deduped;
        return deduped.filter((p) => p.category === cat);
    }, [deduped, cat]);

    const setCat = (c) => {
        if (c === "All") setSearchParams({});
        else setSearchParams({ cat: c });
    };

    return (
        <div className="bg-ink-base text-ink min-h-screen" data-testid="shop-page">
            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-16">
                <div className="border-b border-ink pb-12 mb-12">
                    <p className="section-num mb-4">/ CATALOG · {filtered.length} PRODUCT LINES</p>
                    <h1 className="font-display text-5xl sm:text-7xl font-bold text-ink tracking-tight leading-[0.92]">Research<br /><span className="text-neon">catalog.</span></h1>
                    <p className="mt-6 text-ink-dim max-w-2xl">One page per compound. Dosage options available on the product page. Every batch third-party tested by Janoshik.</p>
                </div>

                <div className="mb-12 p-6 md:p-8 border border-gold/30 bg-gradient-to-r from-gold/5 via-transparent to-neon/5 relative overflow-hidden grid md:grid-cols-12 gap-6 items-center" data-testid="retatrutide-callout">
                    <div className="md:col-span-7">
                        <p className="overline text-gold mb-2">★ BULK DEALS · EVERY PRODUCT</p>
                        <h3 className="font-display text-2xl md:text-3xl font-bold text-ink leading-tight">Stack any compound: <span className="text-neon">3 = 10% off</span>, <span className="text-neon">5 = 15% off</span>, <span className="text-gold">10 = 20% off</span>.</h3>
                        <p className="mt-3 text-sm text-ink-dim">Discounts auto-apply at checkout when you hit a tier. Mix & match within any product family.</p>
                    </div>
                    <div className="md:col-span-5 flex md:justify-end gap-3 flex-wrap">
                        <Link to="/product/retatrutide-60mg" className="inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-6 py-3 font-bold text-sm transition-colors">FEATURED: RETATRUTIDE →</Link>
                    </div>
                </div>

                {cat === "EU Warehouse" && false && (
                    <div data-testid="eu-warehouse-banner-removed" />
                )}                <div className="grid md:grid-cols-[220px_1fr] gap-10">
                    <aside className="space-y-px" data-testid="shop-filters">
                        <div className="overline text-ink-mute mb-4">FILTERS</div>
                        {["All", ...categories].map((c) => (
                            <button
                                key={c}
                                onClick={() => setCat(c)}
                                className={`w-full text-left px-3 py-3 text-xs font-mono uppercase tracking-[0.15em] border-l-2 transition-all ${cat === c ? "border-neon text-neon bg-ink-surface" : "border-transparent text-ink-dim hover:text-ink hover:border-ink-strong"}`}
                                data-testid={`category-filter-${c.replace(/\s+/g,'-').replace(/&/g,'and').toLowerCase()}`}
                            >
                                {c}
                            </button>
                        ))}

                        <div className="mt-10 p-5 bg-ink-surface border border-ink">
                            <div className="overline text-neon mb-2">QA</div>
                            <p className="text-xs text-ink-dim leading-relaxed">Every batch independently assayed. COA available on each product page via Janoshik verification.</p>
                        </div>
                    </aside>

                    <div>
                        <div className="flex justify-between items-center mb-6 text-xs font-mono uppercase tracking-[0.15em] text-ink-mute">
                            <span data-testid="shop-result-count">{loading ? "LOADING…" : `${filtered.length} PRODUCT LINE${filtered.length === 1 ? "" : "S"}`}</span>
                            <span className="text-neon">{cat.toUpperCase()}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shop-grid">
                            {filtered.length > 0
                                ? filtered.map((p, idx) => <ProductCard key={p.id} product={p} index={idx} />)
                                : loading && Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={`sk-${i}`} />)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
